/**
 * Merge Location-backed and Places-backed GettingThere options (FRE-1292).
 *
 * Precedence:
 * - **Location wins** per layer when it already supplies that layer
 *   (pipeline / Ticketmaster enrichment).
 * - Places **walk + transit are atomic**: `mapPlacesToGettingThereOptions`
 *   derives both rows from the same nearest stop. Taking only one Places
 *   layer would pair a walk distance/time with a different stop than the
 *   transit name shown. Inject the Places walk/transit pair only when
 *   Location supplies neither layer.
 * - Places parking still fills independently when Location has no parking.
 *
 * Provider for the Places branch: Google Places API (New) Nearby Search.
 */
import type {
  GettingThereLayer,
  GettingThereOption,
} from '@/components/organisms/GettingThereCard';

const LAYER_ORDER: ReadonlyArray<GettingThereLayer> = ['walk', 'transit', 'parking', 'bike'];

const PLACES_TRANSIT_PAIR_LAYERS = new Set<GettingThereLayer>(['walk', 'transit']);

export function mergeGettingThereOptions(
  fromLocation: readonly GettingThereOption[],
  fromPlaces: readonly GettingThereOption[],
): GettingThereOption[] {
  const byLayer = new Map<GettingThereLayer, GettingThereOption>();
  for (const option of fromLocation) {
    if (!byLayer.has(option.layer)) {
      byLayer.set(option.layer, option);
    }
  }

  const locationHasWalkOrTransit = byLayer.has('walk') || byLayer.has('transit');

  for (const option of fromPlaces) {
    if (byLayer.has(option.layer)) continue;
    if (PLACES_TRANSIT_PAIR_LAYERS.has(option.layer) && locationHasWalkOrTransit) {
      // Skip orphan Places walk/transit — would desync stop name vs walk metric.
      continue;
    }
    byLayer.set(option.layer, option);
  }

  return LAYER_ORDER.flatMap((layer) => {
    const option = byLayer.get(layer);
    return option ? [option] : [];
  });
}
